import { useEffect, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { ArrowUpRight, Download, Menu, X } from "lucide-react";
import { usePwaInstall } from "@/hooks/usePwaInstall";

const navigation = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "About us", to: "/about" },
  { label: "Projects", to: "/projects" },
];

export default function SiteLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { canInstall, dismiss, dismissed, install, isInstalled, isIOS } = usePwaInstall();
  const showInstallPrompt = !isInstalled && !dismissed && (canInstall || isIOS);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileOpen(false);
    };

    document.body.style.overflow = mobileOpen ? "hidden" : "";
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [mobileOpen]);

  return (
    <div className="min-h-screen bg-[#f4f6f7] text-[#263746]">
      <header className="site-header">
        <div className="mx-auto flex h-[78px] max-w-[1440px] items-center justify-between px-5 sm:px-8 md:h-[96px] md:px-10 lg:px-14">
          <Link to="/" className="group flex items-center gap-3" onClick={() => setMobileOpen(false)}>
            <img src="/image/prime-essence-logo.webp" alt="Prime Essence Engineering Limited" className="site-logo" />
          </Link>

          <nav className="desktop-navigation hidden items-center gap-6 md:flex lg:gap-8" aria-label="Main navigation">
            {navigation.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) => `nav-link ${isActive ? "nav-link-active" : ""}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:block">
            <Link to="/contact" className="nav-cta">
              Start a conversation <ArrowUpRight size={15} strokeWidth={2.2} />
            </Link>
          </div>

          <button
            className="mobile-menu-toggle flex h-11 w-11 items-center justify-center border border-[#d9e1e7] text-[#263746] md:hidden"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-navigation-drawer"
            onClick={() => setMobileOpen((open) => !open)}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        <div className={`mobile-menu-layer ${mobileOpen ? "mobile-menu-layer-open" : ""}`} aria-hidden={!mobileOpen} onClick={() => setMobileOpen(false)}>
          <aside id="mobile-navigation-drawer" className={`mobile-navigation-drawer ${mobileOpen ? "mobile-navigation-drawer-open" : ""}`} aria-label="Mobile navigation" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-[#d9e1e7] px-5 py-5">
              <Link to="/" className="flex items-center gap-3" onClick={() => setMobileOpen(false)}>
                <img src="/image/prime-essence-logo.webp" alt="Prime Essence Engineering Limited" className="site-logo site-logo-mobile" />
              </Link>
              <button className="flex h-10 w-10 items-center justify-center border border-[#d9e1e7] text-[#263746]" aria-label="Close menu" onClick={() => setMobileOpen(false)}><X size={20} /></button>
            </div>
            <nav className="flex flex-col px-5 pt-4" aria-label="Mobile navigation links">
              {navigation.map((item, index) => (
                <NavLink key={item.to} to={item.to} end={item.to === "/"} onClick={() => setMobileOpen(false)} className={({ isActive }) => `mobile-nav-link ${isActive ? "mobile-nav-link-active" : ""}`}>
                  <span className="text-[10px] font-bold tracking-[0.18em] text-[#69b3d2]">0{index + 1}</span>{item.label}
                </NavLink>
              ))}
              <Link to="/contact" onClick={() => setMobileOpen(false)} className="mt-6 flex items-center justify-between bg-[#69b3d2] px-5 py-4 text-sm font-bold text-[#263746]">Start a conversation <ArrowUpRight size={17} /></Link>
            </nav>
            <div className="mt-auto border-t border-[#d9e1e7] px-5 py-5 text-xs leading-5 text-[#627481]">Engineering the infrastructure that powers progress across West Africa.</div>
          </aside>
        </div>
      </header>

      {showInstallPrompt && (
        <aside className="pwa-install-banner" aria-label="Install Prime Essence Engineering Limited">
          <div className="flex min-w-0 items-start gap-3">
            <span className="pwa-install-icon" aria-hidden="true"><Download size={18} /></span>
            <div>
              <p className="text-sm font-bold text-[#263746]">Install Prime Essence</p>
              <p className="mt-1 text-xs leading-5 text-[#627481]">
                {isIOS ? "Tap Share, then Add to Home Screen to keep us close." : "Save the web app for quick access and offline viewing."}
              </p>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3 sm:mt-0">
            {canInstall && <button type="button" onClick={install} className="pwa-install-button">Install app</button>}
            <button type="button" onClick={dismiss} className="pwa-install-dismiss" aria-label="Dismiss install prompt"><X size={17} /></button>
          </div>
        </aside>
      )}

      <main>
        <Outlet />
      </main>

      <footer className="border-t border-[#d9e1e7] bg-[#263746] text-[#f4f6f7]">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-14 sm:px-8 lg:grid-cols-[1.35fr_1fr_1fr_1.1fr] lg:px-14 lg:py-20">
          <div>
            <img src="/image/prime-essence-logo.webp" alt="Prime Essence Engineering Limited" className="site-logo site-logo-footer" />
            <p className="mt-6 max-w-xs text-sm leading-7 text-[#aebbc4]">Engineering the infrastructure that powers progress across West Africa.</p>
            <p className="mt-8 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#69b3d2]">Est. 2012 · Lagos, Nigeria</p>
          </div>
          <div>
            <p className="eyebrow text-[#69b3d2]">Explore</p>
            <div className="mt-5 flex flex-col gap-3 text-sm text-[#dce5ea]">
              <Link to="/services" className="footer-link">Our services</Link>
              <Link to="/about" className="footer-link">About Prime Essence</Link>
              <Link to="/projects" className="footer-link">Selected projects</Link>
            </div>
          </div>
          <div>
            <p className="eyebrow text-[#69b3d2]">Contact</p>
            <div className="mt-5 flex flex-col gap-3 text-sm leading-6 text-[#dce5ea]">
              <a href="mailto:info@primeessenceengineering.com" className="footer-link whitespace-nowrap text-[10px]">info@primeessenceengineering.com</a>
              <a href="mailto:Support@primeessenceengineering.com" className="footer-link whitespace-nowrap text-[10px]">Support@primeessenceengineering.com</a>
              <a href="tel:+2348025568836" className="footer-link">+234 (0) 802 556 8836</a>
              <a href="tel:+2348021234567" className="footer-link">+234 (0) 802 123 4567</a>
              <span>Victoria Island, Lagos<br />Nigeria</span>
            </div>
          </div>
          <div>
            <p className="eyebrow text-[#69b3d2]">Stay informed</p>
            <p className="mt-5 text-sm leading-6 text-[#aebbc4]">Ideas, insights, and field notes from the people behind the projects.</p>
            <Link to="/contact" className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#f4f6f7] underline decoration-[#69b3d2] underline-offset-4">Get in touch <ArrowUpRight size={15} /></Link>
          </div>
        </div>
        <div className="mx-auto flex max-w-[1440px] flex-col gap-3 border-t border-[#29414a] px-5 py-5 text-[10px] uppercase tracking-[0.16em] text-[#8aa0ad] sm:px-8 md:flex-row md:items-center md:justify-between lg:px-14">
          <span>© {new Date().getFullYear()} Prime Essence Engineering Limited</span>
          <span>Built for demanding environments</span>
        </div>
      </footer>
    </div>
  );
}
